import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import EmptyState from '../../components/ui/EmptyState';
import { supabase } from '../../lib/supabase';
import { subscribePooled } from '../../utils/realtimePoolManager';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatPrice = (p) => {
  if (!p) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(p);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })?.format(new Date(d));
};

const formatTime = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' })?.format(new Date(d));
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })?.format(new Date(d));
};

// ─── Abidjan Communes ─────────────────────────────────────────────────────────

const ABIDJAN_COMMUNES = [
  'Abobo', 'Adjamé', 'Attécoubé', 'Cocody', 'Koumassi',
  'Marcory', 'Plateau', 'Port-Bouët', 'Treichville', 'Yopougon',
  'Bingerville', 'Anyama', 'Songon', 'Songon-Agban',
];

// ─── Status Configs ───────────────────────────────────────────────────────────

const ESCROW_STATUS = {
  DRAFT:          { label: 'Brouillon',        color: 'bg-gray-100 text-gray-600' },
  PENDING:        { label: 'En attente',        color: 'bg-amber-100 text-amber-700' },
  FUNDS_HELD:     { label: 'Séquestre actif',   color: 'bg-brand-gold/20 text-brand-gold' },
  RELEASED:       { label: 'Fonds libérés',     color: 'bg-emerald-100 text-emerald-700' },
  COMPLETED:      { label: 'Complété',          color: 'bg-emerald-100 text-emerald-800' },
  DISPUTE:        { label: 'Litige',            color: 'bg-red-100 text-red-700' },
  DISPUTED:       { label: 'Litige',            color: 'bg-red-100 text-red-700' },
  CANCELLED:      { label: 'Annulé',            color: 'bg-red-100 text-red-600' },
  OFFER_ACCEPTED: { label: 'Offre acceptée',    color: 'bg-indigo-100 text-indigo-700' },
};

const PROPERTY_STATUS = {
  available:  { label: 'Disponible',  color: 'bg-emerald-100 text-emerald-700' },
  pending:    { label: 'En attente',  color: 'bg-amber-100 text-amber-700' },
  sold:       { label: 'Vendu',       color: 'bg-gray-100 text-gray-600' },
  rented:     { label: 'Loué',        color: 'bg-blue-100 text-blue-700' },
  hidden:     { label: 'Masqué',      color: 'bg-red-100 text-red-600' },
};

const APPT_STATUS = {
  scheduled:  { label: 'Planifié',     color: 'bg-blue-100 text-blue-700' },
  confirmed:  { label: 'Confirmé',     color: 'bg-emerald-100 text-emerald-700' },
  completed:  { label: 'Terminé',      color: 'bg-gray-100 text-gray-600' },
  cancelled:  { label: 'Annulé',       color: 'bg-red-100 text-red-600' },
  rescheduled:{ label: 'Reprogrammé',  color: 'bg-amber-100 text-amber-700' },
};

// ─── Live Sync Indicator ──────────────────────────────────────────────────────

const LiveBadge = ({ active }) => (
  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
    active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
    {active ? 'Sync live' : 'Hors ligne'}
  </span>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, sub, iconBg, iconColor }) => (
  <div className="bg-white dark:bg-card rounded-2xl border border-border/50 p-4 flex items-start gap-3">
    <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
      <Icon name={icon} size={18} className={iconColor} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] text-text-secondary font-medium uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-text-primary mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-text-secondary mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Property Verification Card ───────────────────────────────────────────────

const PropertyVerificationCard = ({ property, onVerify, onUpdateStatus }) => {
  const [verifying, setVerifying] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState('');

  const handleVerify = async () => {
    setVerifying(true);
    try {
      await supabase
        ?.from('properties')
        ?.update({ notary_verified: true, updated_at: new Date()?.toISOString() })
        ?.eq('id', property?.id);
      onVerify?.(property?.id);
    } catch (_) {}
    setVerifying(false);
  };

  const handleStatusUpdate = async (newStatus) => {
    setStatusUpdating(true);
    try {
      await supabase
        ?.from('properties')
        ?.update({ status: newStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', property?.id);
      onUpdateStatus?.(property?.id, newStatus);
    } catch (_) {}
    setStatusUpdating(false);
  };

  const statusCfg = PROPERTY_STATUS?.[property?.status] || PROPERTY_STATUS?.available;

  return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 overflow-hidden">
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-start gap-3 p-4 hover:bg-surface/40 transition-colors text-left"
      >
        <div className="w-12 h-12 rounded-xl bg-brand-earth/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Building2" size={20} className="text-brand-earth" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-bold text-text-primary truncate">{property?.title || 'Propriété sans titre'}</p>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {property?.notary_verified && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 border border-emerald-200 rounded-full">
                  <Icon name="ShieldCheck" size={9} className="text-emerald-600" />
                  <span className="text-[9px] font-bold text-emerald-700">Vérifié</span>
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusCfg?.color}`}>
                {statusCfg?.label}
              </span>
            </div>
          </div>
          <p className="text-xs text-text-secondary mt-1 flex items-center gap-1">
            <Icon name="MapPin" size={10} />
            {property?.address || '—'} · {property?.commune || '—'}
          </p>
          <p className="text-xs font-semibold text-brand-earth mt-0.5">{formatPrice(property?.price)}</p>
        </div>
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-text-secondary flex-shrink-0 mt-1" />
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border/30 space-y-3 pt-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-text-secondary">Type</p>
              <p className="font-semibold text-text-primary capitalize">{property?.property_type || '—'}</p>
            </div>
            <div>
              <p className="text-text-secondary">Commune</p>
              <p className="font-semibold text-text-primary">{property?.commune || '—'}</p>
            </div>
            <div>
              <p className="text-text-secondary">Superficie</p>
              <p className="font-semibold text-text-primary">{property?.area ? `${property?.area} m²` : '—'}</p>
            </div>
            <div>
              <p className="text-text-secondary">Ajouté le</p>
              <p className="font-semibold text-text-primary">{formatDate(property?.created_at)}</p>
            </div>
          </div>

          {/* Quick status update */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Mettre à jour le statut</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(PROPERTY_STATUS)?.map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => handleStatusUpdate(key)}
                  disabled={statusUpdating || property?.status === key}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                    property?.status === key
                      ? cfg?.color + 'ring-2 ring-offset-1 ring-current' :'bg-border/30 text-text-secondary hover:bg-border/50'
                  } disabled:opacity-60`}
                >
                  {cfg?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Verify button */}
          {!property?.notary_verified && (
            <button
              onClick={handleVerify}
              disabled={verifying}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 transition-colors"
            >
              {verifying ? (
                <><Icon name="Loader" size={14} className="animate-spin" />Vérification…</>
              ) : (
                <><Icon name="ShieldCheck" size={14} />Vérifier sur site</>
              )}
            </button>
          )}
          {property?.notary_verified && (
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
              <Icon name="ShieldCheck" size={14} className="text-emerald-600 flex-shrink-0" />
              <p className="text-xs font-semibold text-emerald-700">Propriété vérifiée sur site</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Escrow Update Card ───────────────────────────────────────────────────────

const EscrowUpdateCard = ({ escrow, onUpdate }) => {
  const [updating, setUpdating] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const AGENT_ESCROW_STATUSES = ['OFFER_SENT', 'OFFER_ACCEPTED', 'PAYMENT_PENDING', 'FUNDS_HELD'];

  const handleUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await supabase
        ?.from('escrow_transactions')
        ?.update({ status: newStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', escrow?.id);
      onUpdate?.(escrow?.id, newStatus);
    } catch (_) {}
    setUpdating(false);
  };

  const cfg = ESCROW_STATUS?.[escrow?.status] || ESCROW_STATUS?.PENDING;

  return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 overflow-hidden">
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center gap-3 p-4 hover:bg-surface/40 transition-colors text-left"
      >
        <div className="w-10 h-10 rounded-xl bg-brand-gold/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Lock" size={16} className="text-brand-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">
            {escrow?.property?.title || escrow?.item_title || escrow?.title || 'Transaction'}
          </p>
          <p className="text-xs text-text-secondary mt-0.5">{formatDateTime(escrow?.created_at)}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-sm font-bold text-brand-earth">
            {formatPrice(escrow?.amount_total || escrow?.total_amount)}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg?.color}`}>{cfg?.label}</span>
        </div>
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-text-secondary ml-1 flex-shrink-0" />
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border/30 pt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-text-secondary">Référence</p>
              <p className="font-mono font-semibold text-text-primary truncate">
                {escrow?.reference || escrow?.paystack_reference || '—'}
              </p>
            </div>
            <div>
              <p className="text-text-secondary">Commune</p>
              <p className="font-semibold text-text-primary">{escrow?.property?.commune || '—'}</p>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Mettre à jour le séquestre</p>
            <div className="flex flex-wrap gap-1.5">
              {AGENT_ESCROW_STATUSES?.map(key => {
                const sCfg = ESCROW_STATUS?.[key] || { label: key, color: 'bg-gray-100 text-gray-600' };
                return (
                  <button
                    key={key}
                    onClick={() => handleUpdate(key)}
                    disabled={updating || escrow?.status === key}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                      escrow?.status === key
                        ? sCfg?.color + 'ring-2 ring-offset-1 ring-current' :'bg-border/30 text-text-secondary hover:bg-border/50'
                    } disabled:opacity-60`}
                  >
                    {sCfg?.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Appointment Card ─────────────────────────────────────────────────────────

const AppointmentCard = ({ appt }) => {
  const cfg = APPT_STATUS?.[appt?.status] || APPT_STATUS?.scheduled;
  const isToday = appt?.scheduled_date
    ? new Date(appt.scheduled_date)?.toDateString() === new Date()?.toDateString()
    : false;

  return (
    <div className={`flex items-start gap-3 p-3.5 rounded-xl border ${isToday ? 'border-brand-earth/30 bg-brand-earth/5' : 'border-border/40 bg-surface/30'}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isToday ? 'bg-brand-earth/15' : 'bg-blue-50'}`}>
        <Icon name="Calendar" size={16} className={isToday ? 'text-brand-earth' : 'text-blue-600'} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-text-primary truncate">
            {appt?.property?.title || appt?.appointment_type || 'Rendez-vous'}
          </p>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${cfg?.color}`}>{cfg?.label}</span>
        </div>
        <p className="text-xs text-text-secondary mt-0.5 flex items-center gap-1">
          <Icon name="Clock" size={10} />
          {isToday ? `Aujourd'hui à ${formatTime(appt?.scheduled_date)}` : formatDateTime(appt?.scheduled_date)}
        </p>
        {appt?.location && (
          <p className="text-xs text-text-secondary mt-0.5 flex items-center gap-1 truncate">
            <Icon name="MapPin" size={10} />
            {appt?.location}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Main AgentMobileDashboard ────────────────────────────────────────────────

const AgentMobileDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('properties');
  const [properties, setProperties] = useState([]);
  const [escrows, setEscrows] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [selectedCommune, setSelectedCommune] = useState('');
  const [lastSync, setLastSync] = useState(null);
  const [syncCount, setSyncCount] = useState(0);

  const fetchAll = useCallback(async () => {
    if (!user?.id) return;
    try {
      const communeFilter = selectedCommune || null;

      const [propRes, escrowRes, apptRes] = await Promise.all([
        supabase
          ?.from('properties')
          ?.select('id, title, address, commune, property_type, price, status, notary_verified, area, created_at')
          ?.order('created_at', { ascending: false })
          ?.limit(30),
        supabase
          ?.from('escrow_transactions')
          ?.select(`id, title, item_title, amount_total, total_amount, status, reference, paystack_reference, created_at, updated_at, property:properties(id, title, commune)`)
          ?.in('status', ['OFFER_SENT', 'OFFER_ACCEPTED', 'PAYMENT_PENDING', 'FUNDS_HELD', 'PENDING'])
          ?.order('updated_at', { ascending: false })
          ?.limit(20),
        supabase
          ?.from('appointments')
          ?.select(`id, appointment_type, scheduled_date, status, location, notes, property:properties(id, title, commune)`)
          ?.gte('scheduled_date', new Date()?.toISOString())
          ?.order('scheduled_date', { ascending: true })
          ?.limit(15),
      ]);

      let props = propRes?.data || [];
      if (communeFilter) props = props?.filter(p => p?.commune === communeFilter);

      setProperties(props);
      setEscrows(escrowRes?.data || []);
      setAppointments(apptRes?.data || []);
      setLastSync(new Date());
      setSyncCount(c => c + 1);
    } catch (_) {}
  }, [user?.id, selectedCommune]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchAll();
      setLoading(false);
    })();
  }, [fetchAll]);

  // Realtime sync
  useEffect(() => {
    if (!user?.id) return;
    const unsub = subscribePooled({
      channelName: `agent-mobile-${user?.id}`,
      listeners: [
        { event: '*', schema: 'public', table: 'properties' },
        { event: '*', schema: 'public', table: 'escrow_transactions' },
        { event: '*', schema: 'public', table: 'appointments' },
      ],
      debounceMs: 500,
      onStatusChange: (status) => setIsLive(status === 'SUBSCRIBED'),
      callback: () => { fetchAll(); },
    });
    return () => unsub();
  }, [user?.id, fetchAll]);

  const handlePropertyVerified = (id) => {
    setProperties(prev => prev?.map(p => p?.id === id ? { ...p, notary_verified: true } : p));
  };

  const handlePropertyStatusUpdate = (id, status) => {
    setProperties(prev => prev?.map(p => p?.id === id ? { ...p, status } : p));
  };

  const handleEscrowUpdate = (id, status) => {
    setEscrows(prev => prev?.map(e => e?.id === id ? { ...e, status } : e));
  };

  const verifiedCount = properties?.filter(p => p?.notary_verified)?.length;
  const activeEscrows = escrows?.filter(e => e?.status === 'FUNDS_HELD')?.length;
  const todayAppts = appointments?.filter(a =>
    new Date(a?.scheduled_date)?.toDateString() === new Date()?.toDateString()
  )?.length;

  const TABS = [
    { id: 'properties', label: 'Propriétés', icon: 'Building2', count: properties?.length },
    { id: 'escrow', label: 'Séquestre', icon: 'Lock', count: activeEscrows },
    { id: 'appointments', label: 'Rendez-vous', icon: 'Calendar', count: todayAppts },
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard Agent Mobile — KapitalEstates</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Helmet>

      <div className="min-h-screen bg-surface dark:bg-background">
        <Header />

        <div className="max-w-lg mx-auto px-4 pt-4 pb-24">

          {/* ── Top Bar ── */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-lg font-bold text-text-primary">Dashboard Agent</h1>
              <p className="text-xs text-text-secondary mt-0.5">
                {lastSync ? `Sync: ${formatTime(lastSync?.toISOString())}` : 'Chargement…'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <LiveBadge active={isLive} />
              <button
                onClick={fetchAll}
                className="w-8 h-8 rounded-xl bg-brand-earth/10 flex items-center justify-center hover:bg-brand-earth/20 transition-colors"
              >
                <Icon name="RefreshCw" size={14} className="text-brand-earth" />
              </button>
            </div>
          </div>

          {/* ── Commune Filter ── */}
          <div className="mb-4">
            <select
              value={selectedCommune}
              onChange={e => setSelectedCommune(e?.target?.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-border/60 bg-white dark:bg-card text-sm text-text-primary focus:outline-none focus:border-brand-earth/60 transition-colors"
            >
              <option value="">Toutes les communes d'Abidjan</option>
              {ABIDJAN_COMMUNES?.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* ── Stats Row ── */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <StatCard icon="ShieldCheck" label="Vérifiés" value={verifiedCount}
              sub={`/${properties?.length}`} iconBg="bg-emerald-50" iconColor="text-emerald-600" />
            <StatCard icon="Lock" label="Séquestre actif" value={activeEscrows}
              sub="en cours" iconBg="bg-brand-gold/10" iconColor="text-brand-gold" />
            <StatCard icon="Calendar" label="RDV aujourd'hui" value={todayAppts}
              sub="planifiés" iconBg="bg-blue-50" iconColor="text-blue-600" />
          </div>

          {/* ── Payout Verification Shortcut ── */}
          <button
            onClick={() => navigate('/agent-payout-verification')}
            className="w-full flex items-center gap-3 px-4 py-3 mb-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-2xl shadow-sm hover:from-emerald-700 hover:to-emerald-800 transition-all"
          >
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Icon name="Banknote" size={18} className="text-white" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold">Vérification des Paiements</p>
              <p className="text-xs text-emerald-100">Approuver les règlements en attente</p>
            </div>
            <Icon name="ChevronRight" size={16} className="text-white/70" />
          </button>

          {/* ── Tabs ── */}
          <div className="flex gap-1 bg-muted/40 rounded-xl p-1 mb-4">
            {TABS?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === tab?.id
                    ? 'bg-white dark:bg-card text-brand-earth shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name={tab?.icon} size={13} />
                <span className="hidden sm:inline">{tab?.label}</span>
                {tab?.count > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                    activeTab === tab?.id ? 'bg-brand-earth/15 text-brand-earth' : 'bg-border/40 text-text-secondary'
                  }`}>
                    {tab?.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── Tab Content ── */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3]?.map(i => (
                <div key={i} className="animate-pulse h-20 bg-border/30 rounded-2xl" />
              ))}
            </div>
          ) : (
            <>
              {activeTab === 'properties' && (
                <div className="space-y-3">
                  {properties?.length === 0 ? (
                    <EmptyState iconName="Building2" title="Aucune propriété" description="Aucune propriété trouvée pour cette commune." size="sm" />
                  ) : (
                    properties?.map(prop => (
                      <PropertyVerificationCard
                        key={prop?.id}
                        property={prop}
                        onVerify={handlePropertyVerified}
                        onUpdateStatus={handlePropertyStatusUpdate}
                      />
                    ))
                  )}
                </div>
              )}

              {activeTab === 'escrow' && (
                <div className="space-y-3">
                  {escrows?.length === 0 ? (
                    <EmptyState iconName="Lock" title="Aucun séquestre actif" description="Les transactions séquestre actives apparaîtront ici." size="sm" />
                  ) : (
                    escrows?.map(escrow => (
                      <EscrowUpdateCard
                        key={escrow?.id}
                        escrow={escrow}
                        onUpdate={handleEscrowUpdate}
                      />
                    ))
                  )}
                </div>
              )}

              {activeTab === 'appointments' && (
                <div className="space-y-3">
                  {appointments?.length === 0 ? (
                    <EmptyState iconName="Calendar" title="Aucun rendez-vous" description="Vos prochains rendez-vous apparaîtront ici." size="sm" />
                  ) : (
                    appointments?.map(appt => (
                      <AppointmentCard key={appt?.id} appt={appt} />
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Bottom Nav (mobile) ── */}
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-card border-t border-border/50 px-4 py-2 flex items-center justify-around z-40 sm:hidden">
          {TABS?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-xl transition-colors ${
                activeTab === tab?.id ? 'text-brand-earth' : 'text-text-secondary'
              }`}
            >
              <Icon name={tab?.icon} size={20} />
              <span className="text-[10px] font-semibold">{tab?.label}</span>
              {tab?.count > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-earth text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                  {tab?.count}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={() => navigate('/mon-espace')}
            className="flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-xl text-text-secondary"
          >
            <Icon name="User" size={20} />
            <span className="text-[10px] font-semibold">Profil</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default AgentMobileDashboard;
